import { useState } from 'react'
import HelpPopover from '../help/HelpPopover'

/**
 * "Description" — what this document establishes, in the inventor's own words
 * (figure S2's right column).
 *
 * It is editable from here because the figure draws the pencil, and because the
 * page that used to own the correction form is gone: this is now the only place
 * on the document screen where a person can say what the file actually is.
 * Where the record carries nothing, it says so rather than showing an empty box.
 */
export interface DocumentDescriptionCardProps {
  description: string
  onSave?: (next: string) => Promise<void>
  /** Deleting the document. It lives inside the Edit panel because the figure's
   * header row has four actions and none of them is a menu — and because a
   * destructive action belongs where a person has already decided to change
   * this document, not one click from every visit. */
  onDelete?: () => void
}

export default function DocumentDescriptionCard({ description, onSave, onDelete }: DocumentDescriptionCardProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(description)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    if (!onSave) return
    setSaving(true)
    setError('')
    try {
      await onSave(draft.trim())
      setEditing(false)
    } catch (e) {
      setError((e as Error)?.message || 'That did not save. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section data-part="DocumentDescriptionCard" className="light-card p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          <span aria-hidden>📝</span> <span>Description</span>
          <HelpPopover
            topic="concept-document-description"
            title="Description"
            body="What this document establishes, in your words. The analysis reads it alongside the file itself, so a clear sentence here makes every claim drawn from this document easier to place."
            size={14}
          />
        </h3>
        {onSave && !editing && (
          <button
            type="button"
            onClick={() => { setDraft(description); setError(''); setEditing(true) }}
            className="rounded-lg border border-sky-200 px-2.5 py-1 text-[12px] font-semibold text-iflow-700 hover:bg-sky-50"
          >
            ✎ Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            aria-label="Description"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[12.5px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-iflow-700 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-iflow-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            {error && <span className="text-[11.5px] text-red-600">{error}</span>}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="ml-auto text-[12px] font-semibold text-red-600 hover:text-red-700 hover:underline"
              >
                Delete this document
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[12.5px] leading-relaxed text-gray-700">
          {description || 'No description has been recorded for this document yet.'}
        </p>
      )}
    </section>
  )
}
